import React from 'react';

/**
 * Custom styled feature icons for landing page
 * Uses the idemenu purple/indigo color scheme
 */

// Wrapper component for consistent styling
const IconWrapper = ({ children, className = '', gradient = 'purple' }) => {
  const gradients = {
    purple: 'from-purple-500 to-indigo-600',
    amber: 'from-amber-400 to-orange-500',
    green: 'from-emerald-400 to-teal-500',
    blue: 'from-blue-400 to-cyan-500',
    pink: 'from-pink-400 to-rose-500',
  };

  return (
    <div className={`relative w-16 h-16 ${className}`}>
      {/* Glow effect */}
      <div className={`absolute inset-0 bg-gradient-to-br ${gradients[gradient]} rounded-2xl opacity-20 blur-xl`} />
      {/* Icon container */}
      <div className={`relative w-full h-full bg-gradient-to-br ${gradients[gradient]} rounded-2xl flex items-center justify-center shadow-lg`}>
        {children}
      </div>
    </div>
  );
};

// Quick Setup / Lightning Bolt Icon
export const QuickSetupIcon = ({ className }) => (
  <IconWrapper className={className} gradient="purple">
    <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="currentColor">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  </IconWrapper>
);

// Multi-Language / Globe Icon
export const MultiLanguageIcon = ({ className }) => (
  <IconWrapper className={className} gradient="blue">
    <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  </IconWrapper>
);

// Custom Design / Palette Icon
export const CustomDesignIcon = ({ className }) => (
  <IconWrapper className={className} gradient="pink">
    <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="currentColor">
      <path d="M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10c1.38 0 2.5-1.12 2.5-2.5 0-.61-.23-1.2-.64-1.67-.08-.1-.13-.21-.13-.33 0-.28.22-.5.5-.5H16c3.31 0 6-2.69 6-6 0-4.96-4.49-9-10-9zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 8 6.5 8 8 8.67 8 9.5 7.33 11 6.5 11zm3-4C8.67 7 8 6.33 8 5.5S8.67 4 9.5 4s1.5.67 1.5 1.5S10.33 7 9.5 7zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 4 14.5 4s1.5.67 1.5 1.5S15.33 7 14.5 7zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 8 17.5 8s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
    </svg>
  </IconWrapper>
);

// Smart Stats / Chart Icon
export const SmartStatsIcon = ({ className }) => (
  <IconWrapper className={className} gradient="green">
    <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 20V10" />
      <path d="M12 20V4" />
      <path d="M6 20v-6" />
    </svg>
  </IconWrapper>
);

// QR Code Icon
export const QRCodeIcon = ({ className }) => (
  <IconWrapper className={className} gradient="amber">
    <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="currentColor">
      <path d="M3 11h8V3H3v8zm2-6h4v4H5V5zM3 21h8v-8H3v8zm2-6h4v4H5v-4zM13 3v8h8V3h-8zm6 6h-4V5h4v4zM13 13h2v2h-2zM15 15h2v2h-2zM13 17h2v2h-2zM17 13h2v2h-2zM19 15h2v2h-2zM17 17h2v2h-2zM15 19h2v2h-2zM19 19h2v2h-2z" />
    </svg>
  </IconWrapper>
);

// Sync / Refresh Icon
export const SyncIcon = ({ className }) => (
  <IconWrapper className={className} gradient="green">
    <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
    </svg>
  </IconWrapper>
);

// Menu / List Icon
export const MenuIcon = ({ className }) => (
  <IconWrapper className={className} gradient="purple">
    <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
      <circle cx="8" cy="6" r="1" fill="currentColor" />
      <circle cx="8" cy="12" r="1" fill="currentColor" />
      <circle cx="8" cy="18" r="1" fill="currentColor" />
    </svg>
  </IconWrapper>
);

// Animated Step Number Component
export const StepNumber = ({ number, color = 'purple' }) => {
  const colors = {
    purple: 'from-purple-500 to-indigo-600',
    amber: 'from-amber-400 to-orange-500',
    green: 'from-emerald-400 to-teal-500',
  };

  return (
    <div className="relative inline-flex items-center justify-center">
      {/* Animated ring */}
      <div className={`absolute w-20 h-20 rounded-full bg-gradient-to-br ${colors[color]} opacity-20 animate-ping`} style={{ animationDuration: '3s' }} />
      {/* Number circle */}
      <div className={`relative w-16 h-16 rounded-full bg-gradient-to-br ${colors[color]} flex items-center justify-center shadow-lg`}>
        <span className="text-2xl font-bold text-white">{number}</span>
      </div>
    </div>
  );
};

// Floating decoration shapes for sections
export const FloatingShapes = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Large circle - top right */}
    <div className="absolute -top-20 -right-20 w-96 h-96 bg-purple-200 rounded-full opacity-20 blur-3xl" />
    
    {/* Small circle - bottom left */}
    <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-indigo-200 rounded-full opacity-20 blur-2xl" />
    
    {/* Dots pattern */}
    <svg className="absolute top-20 left-10 w-24 h-24 text-purple-300 opacity-30" viewBox="0 0 100 100">
      {[0, 1, 2, 3, 4].map(row => (
        [0, 1, 2, 3, 4].map(col => (
          <circle key={`${row}-${col}`} cx={10 + col * 20} cy={10 + row * 20} r="3" fill="currentColor" />
        ))
      ))}
    </svg>
    
    {/* Diagonal lines */}
    <svg className="absolute bottom-20 right-10 w-32 h-32 text-indigo-300 opacity-20" viewBox="0 0 100 100">
      {[0, 1, 2, 3, 4, 5].map(i => (
        <line key={i} x1={i * 20} y1="0" x2={i * 20 + 100} y2="100" stroke="currentColor" strokeWidth="2" />
      ))}
    </svg>
  </div>
);

// Trust badge icons with subtle animations
export const TrustBadge = ({ icon, text }) => (
  <div className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5">
    <span className="text-xl">{icon}</span>
    <span className="text-sm font-medium text-gray-700">{text}</span>
  </div>
);

export default {
  QuickSetupIcon,
  MultiLanguageIcon,
  CustomDesignIcon,
  SmartStatsIcon,
  QRCodeIcon,
  SyncIcon,
  MenuIcon,
  StepNumber,
  FloatingShapes,
  TrustBadge,
};
